import { useEffect } from 'react';
import { useOrgStore } from '@/stores/orgStore';
import { apiFetch } from '@/lib/api';
import { applyTheme, clearOrgTheme, type ResolvedTheme } from '@/lib/orgTheme';
import { setLocaleSettings } from '@/lib/localeFormatters';
import { useLocaleStore } from '@/stores/localeStore';

/**
 * Hook that fetches and applies the active organization's resolved theme
 * (platform defaults merged with org overrides).
 * Re-fetches when activeOrgId changes. Clears theme on unmount.
 */
export function useOrgTheme(): void {
  const activeOrgId = useOrgStore(s => s.activeOrgId);
  const updateLocaleStore = useLocaleStore(s => s.updateSettings);

  useEffect(() => {
    let cancelled = false;

    /** Apply locale settings to both the module-level variable and the Zustand store */
    const applyLocale = (d: Record<string, unknown>) => {
      const partial = {
        dateFormat: (d.dateFormat as string) || undefined,
        timeFormat: (d.timeFormat as string) || undefined,
        thousandsSeparator: (d.thousandsSeparator as string) || undefined,
        decimalSeparator: (d.decimalSeparator as string) || undefined,
        currencySymbol: (d.currencySymbol as string) || undefined,
        currencyFormat: (d.currencyFormat as string) || undefined,
      };
      setLocaleSettings(partial);
      updateLocaleStore(partial);
    };

    if (!activeOrgId) {
      clearOrgTheme();
      // Still load platform theme locale settings for admin pages
      (async () => {
        try {
          const res = await apiFetch('/api/admin/platform-theme');
          if (cancelled || !res.ok) return;
          const json = await res.json();
          if (!cancelled && json?.data) {
            applyLocale(json.data);
          }
        } catch {
          // Silently fail — locale defaults are reasonable
        }
      })();
      return () => { cancelled = true; };
    }

    const fetchTheme = async () => {
      try {
        const res = await apiFetch(`/api/orgs/${activeOrgId}/resolved-theme`);
        if (cancelled) return;

        if (res.ok) {
          const json = await res.json();
          if (!cancelled && json?.data) {
            applyTheme(json.data as ResolvedTheme);
            applyLocale(json.data);
          }
        }
      } catch {
        // Silently fail — theme is cosmetic
      }
    };

    fetchTheme();

    return () => {
      cancelled = true;
      clearOrgTheme();
    };
  }, [activeOrgId, updateLocaleStore]);
}
